import { useMemo } from 'react';
import styled from '@emotion/styled';
import Checkbox from '@mui/material/Checkbox';
import { DataGrid, gridClasses, type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import type { SmartRoutingEntityType, SmartRoutingSearchEntity } from '@trinity/chat';
import { EmailMailToLink, OpenAddressCellLink } from '../Chat/LinkCells';

type Props = {
  results: SmartRoutingSearchEntity[];
  validEntities: SmartRoutingEntityType[];
  isLoading: boolean;
  selectedEntityKeys: Set<string>;
  onToggleEntity: (result: SmartRoutingSearchEntity) => void;
};

type SmartRoutingTableMode = 'hcp-only' | 'account-only' | 'mixed';

function getEntitySelectionKey(result: SmartRoutingSearchEntity): string {
  return `${result.entityType}:${result.entityId}`;
}

function isHcpResult(
  result: SmartRoutingSearchEntity
): result is Extract<SmartRoutingSearchEntity, { entityType: 'hcp' }> {
  return result.entityType === 'hcp';
}

function isAccountResult(
  result: SmartRoutingSearchEntity
): result is Extract<SmartRoutingSearchEntity, { entityType: 'account' }> {
  return result.entityType === 'account';
}

export const SmartRoutingEntitySelectionTable = ({
  results,
  validEntities,
  isLoading,
  selectedEntityKeys,
  onToggleEntity,
}: Props) => {
  const tableMode = useMemo<SmartRoutingTableMode>(() => {
    const hasAccount = validEntities.includes('account');
    const hasHcp = validEntities.includes('hcp');

    if (hasAccount && hasHcp) {
      return 'mixed';
    }

    if (hasAccount) {
      return 'account-only';
    }

    return 'hcp-only';
  }, [validEntities]);

  const showSpecialtyColumn = useMemo(() => {
    if (tableMode !== 'hcp-only') {
      return false;
    }

    return results.some((result) => {
      if (!isHcpResult(result)) {
        return false;
      }

      return result.specialty?.some((specialty) => specialty.trim().length > 0) ?? false;
    });
  }, [results, tableMode]);

  const showPrimaryAffiliationColumn = useMemo(() => {
    if (tableMode !== 'hcp-only') {
      return false;
    }

    return results.some((result) => {
      if (!isHcpResult(result)) {
        return false;
      }

      return (
        typeof result.primaryAffiliationName === 'string' && result.primaryAffiliationName.trim().length > 0
      );
    });
  }, [results, tableMode]);

  const columns = useMemo<GridColDef<SmartRoutingSearchEntity>[]>(() => {
    const tableColumns: GridColDef<SmartRoutingSearchEntity>[] = [
      {
        field: 'selected',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 68,
        minWidth: 68,
        maxWidth: 68,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams<SmartRoutingSearchEntity>) => {
          const isSelected = selectedEntityKeys.has(getEntitySelectionKey(params.row));

          return (
            <Checkbox
              size="small"
              checked={isSelected}
              onChange={() => {
                onToggleEntity(params.row);
              }}
              slotProps={{
                input: {
                  'aria-label': `Select ${params.row.name}`,
                },
              }}
            />
          );
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        type: 'string',
        sortable: false,
        minWidth: 200,
        flex: 1,
      },
    ];

    const locationColumn: GridColDef<SmartRoutingSearchEntity> = {
      field: 'location',
      headerName: 'Location',
      type: 'string',
      sortable: false,
      minWidth: 240,
      flex: 1.2,
      valueGetter: (_value, row) => row.location.address,
      renderCell: (params: GridRenderCellParams<SmartRoutingSearchEntity>) => {
        const address = params.row.location.address;
        if (!address) {
          return null;
        }

        return <OpenAddressCellLink address={address} />;
      },
    };

    const distanceColumn: GridColDef<SmartRoutingSearchEntity> = {
      field: 'distance',
      headerName: 'Distance',
      type: 'number',
      minWidth: 120,
      maxWidth: 160,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value, row) => row.distance ?? null,
      renderCell: (params: GridRenderCellParams<SmartRoutingSearchEntity>) => {
        const distance = params.row.distance;
        return typeof distance === 'number' ? `${distance.toFixed(1)} mi` : 'N/A';
      },
    };

    if (tableMode === 'hcp-only') {
      if (showSpecialtyColumn) {
        tableColumns.splice(2, 0, {
          field: 'specialty',
          headerName: 'Specialty',
          type: 'string',
          sortable: false,
          minWidth: 110,
          flex: 0.8,
          valueGetter: (_value, row) => {
            if (!isHcpResult(row)) {
              return null;
            }

            return row.specialty?.join(', ') ?? null;
          },
        });
      }

      tableColumns.push(locationColumn);
      tableColumns.push({
        field: 'email',
        headerName: 'Email',
        type: 'string',
        sortable: false,
        minWidth: 140,
        flex: 1,
        valueGetter: (_value, row) => {
          if (!isHcpResult(row)) {
            return null;
          }

          return row.email;
        },
        renderCell: (params: GridRenderCellParams<SmartRoutingSearchEntity>) => {
          if (!isHcpResult(params.row)) {
            return null;
          }

          const email = params.row.email?.trim();
          if (!email) {
            return null;
          }

          return <EmailMailToLink email={email} />;
        },
      });
      tableColumns.push(distanceColumn);

      if (showPrimaryAffiliationColumn) {
        tableColumns.push({
          field: 'primaryAffiliationName',
          headerName: 'Primary Affiliation',
          type: 'string',
          sortable: false,
          minWidth: 220,
          flex: 1,
          valueGetter: (_value, row) => {
            if (!isHcpResult(row)) {
              return null;
            }

            return row.primaryAffiliationName;
          },
        });
      }
    }

    if (tableMode === 'account-only') {
      tableColumns.push({
        field: 'category',
        headerName: 'Category',
        type: 'string',
        minWidth: 140,
        flex: 1,
        sortable: false,
        valueGetter: (_value, row) => {
          if (!isAccountResult(row)) {
            return null;
          }

          return row.category ?? null;
        },
      });
      tableColumns.push(locationColumn);
      tableColumns.push(distanceColumn);
    }

    if (tableMode === 'mixed') {
      tableColumns.push(locationColumn);
      tableColumns.push(distanceColumn);
      tableColumns.push({
        field: 'customerType',
        headerName: 'Customer Type',
        sortable: false,
        type: 'string',
        minWidth: 160,
        flex: 0.8,
        valueGetter: (_value, row) => (row.entityType === 'account' ? 'Account' : 'HCP'),
      });
    }

    return tableColumns;
  }, [onToggleEntity, selectedEntityKeys, showPrimaryAffiliationColumn, showSpecialtyColumn, tableMode]);

  return (
    <ResultsWrapper>
      <DataGrid
        rows={results}
        columns={columns}
        getRowId={(row) => getEntitySelectionKey(row)}
        disableRowSelectionOnClick={true}
        hideFooter={results.length <= 5}
        pageSizeOptions={[5, 10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: {
              page: 0,
              pageSize: 5,
            },
          },
        }}
        loading={isLoading}
        getRowHeight={() => 'auto'}
        sx={{
          border: 0,
          height: '100%',
          '& .MuiDataGrid-columnHeader': {
            fontWeight: 600,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 600,
          },
          [`& .${gridClasses.row}.odd`]: {
            backgroundColor: '#f6f8fa',
          },
          [`& .${gridClasses.row}.even`]: {
            backgroundColor: '#ffffff',
          },
          '& .MuiDataGrid-cell': {
            alignItems: 'center',
          },
        }}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
      />
    </ResultsWrapper>
  );
};

const ResultsWrapper = styled.div({
  marginTop: 15,
  width: '100%',
  height: '350px',
  maxHeight: '60vh',
  overflow: 'hidden',
});
